import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Badge,
} from "reactstrap";
import { Breadcrumbs, H5, H6 } from "../../AbstractElements";
import { titleCase } from "../../utils/case";
import {
  getAddOns,
  deleteAddOn,
} from "../../api/addon.api";
import { toast } from "react-toastify";
import {
  Edit,
  Eye,
  Shuffle,
  Trash2,
} from "react-feather";
import { hasRole } from "../../utils/auth";

const canViewInventory = hasRole(
  "superadmin",
  "finance",
  "operation"
);

const canManageInventory = hasRole(
  "superadmin",
  "finance"
);

const InventoryItem = ({
  item,
  onClick,
  onEdit,
  onBulkAssign,
  onDelete,
}) => {
  const { t } = useTranslation();
  const inventoryType = item.inventoryType || "-";

  const getBadgeColor = () => {
    if (inventoryType === "asset") return "primary";
    if (inventoryType === "consumable") return "warning";
    return "secondary";
  };

  return (
    <Col xxl="3" xl="3" lg="4" md="6">
      <div
        className="prooduct-details-box cursor-pointer"
        style={{
          transition: "0.2s ease",
        }}
      >
        <div className="media p-3">
          <div
            className="d-flex align-items-center justify-content-center bg-light rounded overflow-hidden"
            style={{ width: "80px", height: "80px", minWidth: "80px" }}
          >
            {item.photo ? (
              <img
                src={`${process.env.REACT_APP_FILES_URL}/${item.photo}`}
                alt={item.name}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <i className="fa fa-cube fa-2x text-muted"></i>
            )}
          </div>

          <div className="media-body ms-3">
            <div className="product-name">
              <div className="d-flex justify-content-end mb-1">
                <Badge
                  color={getBadgeColor()}
                  style={{
                    flexShrink: 0,
                  }}
                >
                  {titleCase(t(inventoryType.replace(/_/g, " ")))}
                </Badge>
              </div>

              <H6
                className="mb-0"
                style={{
                  wordBreak: "break-word",
                }}
              >
                {item.name}
              </H6>
            </div>

            <div className="price d-flex mt-2">
              {titleCase(t(item.category?.replace(/_/g, " ") || "-"))}
            </div>

            <div className="price d-flex">
              <div className="text-muted me-2">
                {titleCase(t("quantity"))}
              </div>
              :{" "}
              {item.inventoryType === "non_stock"
                ? "-"
                : item.totalStock ?? "-"}
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              {canManageInventory && (
                <Shuffle
                  size={18}
                  style={{
                    cursor: "pointer",
                    color: "#89b324",
                  }}
                  title={titleCase(t("bulk assign"))}
                  onClick={(e) => {
                    e.stopPropagation();
                    onBulkAssign();
                  }}
                />
              )}

              {canViewInventory && (
                <Eye
                  size={18}
                  style={{
                    cursor: "pointer",
                    color: "#7da8c7",
                  }}
                  title={titleCase(t("edit"))}
                  onClick={onClick}
                />
              )}
            
              {canManageInventory && (
                <>
                  <Edit
                    size={18}
                    style={{
                      cursor: "pointer",
                      color: "#d4a437",
                    }}
                    title={titleCase(t("edit"))}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                  />

                  <Trash2
                    size={18}
                    style={{
                      cursor: "pointer",
                      color: "#dc3545",
                    }}
                    title={titleCase(t("delete"))}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Col>
  );
};

const InventoryMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInventoryItems = async () => {
    try {
      setLoading(true);

      const res = await getAddOns({
        page: 1,
        perPage: 100,
      });

      setInventoryItems(res?.data || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItems();
  }, []);

  const handleCreate = () => {
    navigate("/inventory/create");
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      titleCase(t("delete this inventory item?"))
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteAddOn(id);

      toast.success(
        titleCase(t("inventory item deleted successfully"))
      );

      fetchInventoryItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <React.Fragment>
      <Breadcrumbs
        parent={titleCase(t("inventory"))}
        title={titleCase(t("inventory management"))}
        mainTitle={titleCase(t("inventory management"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <H5>{titleCase(t("inventory list"))}</H5>
                {canManageInventory && (
                  <Button color="success" onClick={handleCreate}>
                    {t("create").toUpperCase()}
                  </Button>
                )}
              </CardHeader>

              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    {titleCase(t("loading"))}...
                  </div>
                ) : inventoryItems.length > 0 ? (
                  <Row>
                    {inventoryItems.map((item) => (
                      <InventoryItem
                        key={item.id}
                        item={item}
                        onClick={() =>
                          navigate(`/inventory/view/${item.id}`)
                        }
                        onEdit={() =>
                          navigate(`/inventory/edit/${item.id}`)
                        }
                        onBulkAssign={() =>
                          navigate(`/inventory/bulk-assign/${item.id}`)
                        }
                        onDelete={() => handleDelete(item.id)}
                      />
                    ))}
                  </Row>
                ) : (
                  <div className="text-center py-4 text-muted">
                    {titleCase(t("no inventory items found"))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default InventoryMain;